import { useState } from "react";
import Modal from "@cloudscape-design/components/modal";
import { Button, SpaceBetween, Box, Textarea, FormField } from "@cloudscape-design/components";
import Alert from "@cloudscape-design/components/alert";
import FileUpload from "@cloudscape-design/components/file-upload";
import { submitFeedback } from "../../services/feedbackService";

const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4 MB
const ACCEPTED_TYPES = [".png", ".jpg", ".jpeg", ".pdf", ".txt"];

const FeedbackModal = ({ visible, onDismiss }) => {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [alertState, setAlertState] = useState(null); // { type, text }

  const handleDismiss = () => {
    setMessage("");
    setFiles([]);
    setFileError(null);
    setAlertState(null);
    onDismiss();
  };

  const handleFileChange = ({ detail }) => {
    setFileError(null);
    const selected = detail.value;
    if (selected.length > 0 && selected[0].size > MAX_FILE_BYTES) {
      setFileError("File must be 4 MB or smaller.");
      setFiles([]);
      return;
    }
    setFiles(selected);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      setAlertState({ type: "error", text: "Please enter a feedback message before submitting." });
      return;
    }
    setSubmitting(true);
    setAlertState(null);
    try {
      await submitFeedback(message.trim(), files.length > 0 ? files[0] : null);
      setAlertState({ type: "success", text: "Thank you! Your feedback has been submitted." });
      setMessage("");
      setFiles([]);
      setTimeout(handleDismiss, 1800);
    } catch {
      setAlertState({
        type: "error",
        text: "Failed to submit feedback. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Submit Feedback"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={submitting}>
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="m">
        {alertState && (
          <Alert type={alertState.type}>{alertState.text}</Alert>
        )}
        <FormField label="Message" description="Describe your feedback, bug, or feature request.">
          <Textarea
            value={message}
            onChange={({ detail }) => setMessage(detail.value)}
            placeholder="Type your feedback here..."
            rows={6}
            disabled={submitting}
          />
        </FormField>
        <FormField
          label={
            <span>
              Attachment <i style={{ fontWeight: "normal" }}> — optional</i>
            </span>
          }
          description={`Accepted: ${ACCEPTED_TYPES.join(", ")}. Max size: 4 MB.`}
          errorText={fileError}
        >
          <FileUpload
            accept={ACCEPTED_TYPES.join(",")}
            value={files}
            onChange={handleFileChange}
            i18nStrings={{
              uploadButtonText: () => "Choose file",
              dropzoneText: () => "Drop file here",
              removeFileAriaLabel: () => "Remove file",
              limitShowFewer: "Show fewer files",
              limitShowMore: "Show more files",
              errorIconAriaLabel: "Error",
            }}
            showFileSize
            showFileThumbnail
            tokenLimit={1}
            disabled={submitting}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default FeedbackModal;
